import React, { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const SteamStatusCard = ({ steamStatus, username }) => {
    if (!steamStatus) {
      return (
        <div className="bg-black/60 border border-purple-500/20 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Gaming Activity</h3>
          <p className="text-slate-400">Unable to fetch Steam status</p>
        </div>
      );
    }
  
    if (!steamStatus.online || !steamStatus.currentGame) {
      return (
        <div className="bg-black/60 border border-purple-500/20 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Gaming Activity</h3>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${steamStatus.online ? 'bg-green-500' : 'bg-slate-500'}`}></div>
            <p className="text-slate-400">
              {steamStatus.online ? 'Online, not playing any games' : 'Offline'}
            </p>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Last updated: {new Date(steamStatus.lastUpdated).toLocaleTimeString()}
          </p>
        </div>
      );
    }
  
    const game = steamStatus.currentGame;
    
    return (
      <div className="bg-black/60 border border-purple-500/20 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Currently Playing</h3>
        
        {game.gameImages?.header && (
          <div className="mb-4">
            <img 
              src={game.gameImages.header} 
              alt={`${game.gameName} header`}
              className="w-full rounded-md"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </div>
        )}
        
        <div className="flex items-center mb-4">
          {game.gameImages?.icon && (
            <img 
              src={game.gameImages.icon} 
              alt={`${game.gameName} icon`}
              className="w-12 h-12 mr-3 rounded"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          )}
          
          <h4 className="text-xl font-bold text-purple-300">{game.gameName}</h4>
        </div>
        
        {game.gameImages?.logo && (
          <div className="mb-4 bg-slate-800/50 p-4 rounded-md flex justify-center">
            <img 
              src={game.gameImages.logo} 
              alt={`${game.gameName} logo`}
              className="max-h-16"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </div>
        )}
        
        <div className="mt-4">
          <a 
            href={`https://store.steampowered.com/app/${game.gameId}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md transition-colors duration-300"
          >
            <span className="mr-2">View on Steam</span>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-4 w-4" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
        
        <p className="text-xs text-slate-500 mt-2">
          Last updated: {new Date(steamStatus.lastUpdated).toLocaleTimeString()}
        </p>
      </div>
    );
  };
  
  export const DiscordStatusCard = ({ discordStatus, username }) => {
    if (!discordStatus) {
      return (
        <div className="bg-black/60 border border-purple-500/20 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Discord Profile</h3>
          <p className="text-slate-400">Unable to fetch Discord status</p>
        </div>
      );
    }
  
    return (
      <div className="bg-black/60 border border-purple-500/20 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Discord Profile</h3>
        
        <div className="flex items-center mb-4">
          {discordStatus.avatarUrl && (
            <img 
              src={discordStatus.avatarUrl} 
              alt="Discord avatar" 
              className="w-12 h-12 rounded-full mr-3 border border-purple-500/30"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          )}
          
          <div>
            <h4 className="text-xl font-bold text-purple-300">
              {discordStatus.username}
              {discordStatus.discriminator && discordStatus.discriminator !== '0' && (
                <span className="text-sm text-purple-400">#{discordStatus.discriminator}</span>
              )}
            </h4>
            
            {discordStatus.status && (
              <div className="flex items-center mt-1">
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  discordStatus.status === 'online' ? 'bg-green-500' : 
                  discordStatus.status === 'idle' ? 'bg-yellow-500' : 
                  discordStatus.status === 'dnd' ? 'bg-red-500' : 'bg-slate-500'
                }`}></div>
                <span className="text-sm text-slate-300 capitalize">{discordStatus.status}</span>
              </div>
            )}
          </div>
        </div>
        
        {discordStatus.bannerUrl && (
          <div className="mb-4 overflow-hidden rounded-md">
            <img 
              src={discordStatus.bannerUrl} 
              alt="Discord banner" 
              className="w-full h-24 object-cover"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </div>
        )}

        {discordStatus.accentColor != null && (
          <div 
            className="h-1 w-full rounded-full mb-4" 
            style={{ backgroundColor: `#${discordStatus.accentColor.toString(16).padStart(6, '0')}` }}
          ></div>
        )}
        
        <p className="text-xs text-slate-500 mt-2">
          Last updated: {new Date(discordStatus.lastUpdated).toLocaleTimeString()}
        </p>
      </div>
    );
  };
  
  // Combined Social Status Container Component - FIXED VERSION
  export const SocialStatusContainer = ({ 
    statusData, 
    isConnecting, 
    isConnected, 
    connectionError,
    profile 
  }) => {
    // Extract status data for each platform
    const steamId = profile?.socials?.find(s => s.platform === 'steam')?.socialId;
    const discordId = profile?.socials?.find(s => s.platform === 'discord')?.socialId;
    
    const steamStatus = steamId ? statusData?.steam?.[steamId] : null;
    const discordStatus = discordId ? statusData?.discord?.[discordId] : null;
  
    // Early returns for loading/error states
    if (isConnecting) {
      return (
        <div className="space-y-4">
          <div className="bg-black/60 border border-purple-500/20 p-6 rounded-lg">
            <p className="text-center text-slate-400 flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connecting to status service...
            </p>
          </div>
        </div>
      );
    }
  
    if (connectionError && !isConnected) {
      return (
        <div className="space-y-4">
          <div className="bg-black/60 border border-red-500/20 p-6 rounded-lg">
            <p className="text-center text-red-400">{connectionError}</p>
          </div>
        </div>
      );
    }
  
    return (
      <motion.div 
        className="space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {steamId && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <SteamStatusCard steamStatus={steamStatus} username={profile.username} />
          </motion.div>
        )}
        
        {discordId && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <DiscordStatusCard discordStatus={discordStatus} username={profile.username} />
          </motion.div>
        )}
  
        {!steamId && !discordId && (
          <div className="bg-black/60 border border-purple-500/20 p-6 rounded-lg">
            <p className="text-center text-slate-400">No social accounts connected</p>
          </div>
        )}
      </motion.div>
    );
};